import { DateTime } from 'luxon';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useUpdate } from 'react-use';

import { accountsSelectors } from 'app-components/flux/accounts';
import { institutionLoginsActions, institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { getSmartFutureTimeLabel, getSmartTimeLabel } from 'app-components/utils/date';

import RefreshIcon from '@mui/icons-material/Refresh';
import type { ChipProps } from '@mui/material';
import { Chip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Fade from '@mui/material/Fade';

import RendersCount from 'components/RendersCount';
import { isAppleCardAccount, isAppleCashAccount } from 'data/appleWallet/utils';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

type AccountRefreshChipProps = ChipProps &
  ({ institutionLoginId: string; accountId?: string } | { accountId: string; institutionLoginId?: string });
const AccountRefreshChip = ({ accountId, institutionLoginId, ...otherProps }: AccountRefreshChipProps) => {
  const account = useAppSelector((state) => accountsSelectors.getAccountById(state, accountId));
  const institutionLogin = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionLoginForId(state, institutionLoginId || account?.institutionLoginId || ''),
  );
  const isConnected = Boolean(account ? account?.isConnected : institutionLogin?.isConnected);
  const isAllInstitutionLoginsRefreshing = useAppSelector(institutionLoginsSelectors.getIsRefreshingAll);
  const institutionLoginsIdsRefreshing = useAppSelector(institutionLoginsSelectors.getIdsRefreshing);

  const isAppleCash = isAppleCashAccount(account);
  const isAppleCard = isAppleCardAccount(account);

  const [localProgress, setLocalProgress] = useState(false);
  const progress = useMemo(
    () =>
      localProgress ||
      isAllInstitutionLoginsRefreshing ||
      institutionLoginsIdsRefreshing.has(institutionLogin?.id || ''),
    [institutionLogin?.id, institutionLoginsIdsRefreshing, isAllInstitutionLoginsRefreshing, localProgress],
  );

  const dispatch = useAppDispatch();

  const lastRefreshSuccessfulAt = useMemo(
    () => institutionLogin?.aggregators?.get?.(0)?.lastRefreshSuccessfulAt,
    [institutionLogin?.aggregators],
  );
  const nextManualRefreshEligibleDataTime = useMemo(
    () => DateTime.fromISO(institutionLogin?.aggregators?.get?.(0)?.nextManualRefreshEligibleAt || ''),
    [institutionLogin?.aggregators],
  );
  const millisBeforeNextManualRefreshEligible =
    nextManualRefreshEligibleDataTime.toMillis() - DateTime.now().toMillis();

  const updateTimeout = useRef(undefined);
  const update = useUpdate();
  useEffect(() => {
    if (updateTimeout.current) {
      clearTimeout(updateTimeout.current);
    }
    if (millisBeforeNextManualRefreshEligible > 0) {
      setTimeout(() => update(), millisBeforeNextManualRefreshEligible);
    }
  }, [millisBeforeNextManualRefreshEligible, update]);

  const showAppleInstructionsTooltip = isAppleCash || isAppleCard;
  const isDisabled = progress || showAppleInstructionsTooltip || millisBeforeNextManualRefreshEligible > 0;

  const tooltip = useMemo(() => {
    if (showAppleInstructionsTooltip) return 'To refresh Apple Cards, use the Simplifi mobile app on iOS.';
    const lastRefreshSuccessfulDateTime = DateTime.fromISO(lastRefreshSuccessfulAt || '');
    const futureTimeLabel = nextManualRefreshEligibleDataTime.isValid
      ? getSmartFutureTimeLabel(nextManualRefreshEligibleDataTime.toISO())
      : '-';
    return `Last updated: ${
      lastRefreshSuccessfulDateTime.isValid
        ? lastRefreshSuccessfulDateTime.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
        : '-'
    }
        \nNext Available: ${futureTimeLabel === '-' ? 'Now' : `in ${futureTimeLabel}`}
    `;
  }, [lastRefreshSuccessfulAt, nextManualRefreshEligibleDataTime, showAppleInstructionsTooltip]);

  const timeLabel = useMemo(() => {
    let label = lastRefreshSuccessfulAt ? getSmartTimeLabel(lastRefreshSuccessfulAt) : '-';
    if (label.endsWith(' minute')) label = label.replace(' minute', ' min');
    else if (label.endsWith(' minutes')) label = label.replace(' minutes', ' min');
    else if (label === '1 hour') label = '1 hr';
    else if (label.endsWith(' hours')) label = label.replace(' hours', ' hrs');
    return `Updated ${label === 'now' || label === '-' || label.includes('/') ? label : `${label} ago`}`;
  }, [lastRefreshSuccessfulAt]);

  return (
    <Fade in={isConnected} timeout={300} mountOnEnter unmountOnExit>
      <Tooltip title={tooltip}>
        <span>
          <RendersCount id={institutionLoginId || accountId} />
          <Chip
            sharedcomponentid='AccountRefreshChip'
            label={timeLabel}
            size='small'
            color='success'
            variant='tonal'
            clickable={!progress || millisBeforeNextManualRefreshEligible <= 0}
            disabled={isDisabled}
            icon={progress ? <CircularProgress size={16} /> : !isDisabled ? <RefreshIcon /> : undefined}
            sx={{
              '&.Mui-disabled': {
                opacity: 0.8,
              },
            }}
            onClick={() => {
              if (institutionLogin?.id) {
                setLocalProgress(true);
                new Promise((resolve, reject) =>
                  dispatch(
                    institutionLoginsActions.refreshAccounts(
                      { institutionLoginId: institutionLogin?.id },
                      { resolve, reject },
                    ),
                  ),
                )
                  .then(() => setLocalProgress(false))
                  .catch((e) => {
                    setLocalProgress(false);
                    assert(false, e);
                  });
              }
            }}
            {...otherProps}
          />
        </span>
      </Tooltip>
    </Fade>
  );
};

export default AccountRefreshChip;
